import { Badge } from 'antd';
import React, { useEffect, useState , useRef, useContext } from 'react'
import { useLocation } from 'react-router';
import MultiSelectEntity from '../RefineSearch/TgMultiSelectEntity'
import FilterWrapper from './FilterWrapper';
import { FilterReAdvisor, filterRefineAdvisors } from "../../Config/config"
import useScrollIntoView from './useScrollIntoView';
import { PeFilterContext } from '../../Contexts/PeFilterContext';

function TAdvisor({ open, accordionClick, options, setOptions, customPathname, label, menu }) {

    const [companyVal, setCompanyVal] = useState("");
    const [fetchedData, setFetchedData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const shouldScroll = open || fetchedData.length > 0;
    const listItemRef = useScrollIntoView(shouldScroll);
    const { accessControl, refineSearchFilters } = useContext(PeFilterContext);
    const location = useLocation();

    const paths = {
        exits: [
            "/pe/investors/exits/pe-vc-investments",
            "/exits/ma/vc-investments",
            "/exits/vc-investments",
            "/exits/pm/vc-investments",
            "/pm/pe-vc-advisors", 
            "/ma-exits/pe-vc-advisors",
            "/ma/pe-vc-advisors",
            "/pe/investors/exits/pm/pe-vc-investments",
            "/pe/companies/exits/pe-vc-investments",
            "/pe/companies/exits/pm/pe-vc-investments"
        ],
        investments: [
            "/pe-vc-investments", 
            "/vc-investments",
            "/pe/transaction-advisors/pe-vc-advisors", 
            "/vc/investors/vc-investments", 
            "/vc/transaction-advisors/vc-advisors"
        ]
    };

    const currentPath = location.pathname;

    const checkPath = paths.exits.some(path => currentPath.includes(path)) ? 2
        : paths.investments.some(path => currentPath.includes(path)) ? 1 : null;

    // console.log("checkPath:", checkPath);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (companyVal.trim() !== "") {
                fetchData();
            } else {
                // Don't clear options on empty search - keep selected items
                setShowDropdown(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [companyVal]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []); 

    // Auto-scroll when dropdown opens and goes below visible screen
    useEffect(() => {
        if (showDropdown && dropdownRef.current && inputRef.current) {
            setTimeout(() => {
                const dropdownRect = dropdownRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight;

                // Check if dropdown bottom goes below viewport
                if (dropdownRect.bottom > viewportHeight - 20) {
                    // Scroll to bring the input into view with some padding
                    inputRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest'
                    });
                }
            }, 150);
        }
    }, [showDropdown, fetchedData]); 

    const fetchData = () => {
        const payload = {
            search_key: companyVal,
            check_starts_with : true,
            category : menu,
            advisor_type: "T"
        };

        const filter = customPathname?.includes("/re/") ? FilterReAdvisor : filterRefineAdvisors;

        if (customPathname?.includes("/ma/")) {
            payload.module = "ma";
            payload.industry_ids = accessControl?.maindustries?.id?.length > 0 ? accessControl?.maindustries?.id : [];
            payload.country_ids = accessControl?.ma_countries?.id?.length ? accessControl?.ma_countries?.id : [];
            payload.region_ids =  accessControl?.ma_regions?.id?.length ? accessControl?.ma_regions?.id : [];
            payload.state_ids =  accessControl?.ma_stage?.id?.length ? accessControl?.ma_states?.id : [];
            payload.city_ids =  accessControl?.ma_cities?.id?.length ? accessControl?.ma_cities?.id : [];
        } else if (customPathname?.includes("/re/")) {
            payload.type = "T";
        } else if (customPathname?.includes("/pe")){
            payload.module = "pe";
            payload.type = checkPath;
            payload.industry_ids = accessControl?.peindustries?.id?.length > 0 ? accessControl?.peindustries?.id : [];
            payload.tag_ids = accessControl?.pe_tags?.id?.length > 0 ? accessControl.pe_tags.name : [];
            payload.stage_ids =  accessControl?.pe_stage?.id?.length > 0 ? accessControl.pe_stage : [];
            payload.deal_range_usd_min = accessControl?.pe_deal_range?.from;
            payload.deal_range_usd_max =  accessControl?.pe_deal_range?.to;
            payload.country_ids = accessControl?.pe_countries?.id?.length ? accessControl?.pe_countries?.id : [];
            payload.region_ids =  accessControl?.pe_regions?.id?.length ? accessControl?.pe_regions?.id : [];
            payload.states_ids =  accessControl?.pe_states?.id?.length ? accessControl?.pe_states?.id : [];
            payload.city_ids =  accessControl?.pe_cities?.id?.length ? accessControl?.pe_cities?.id : [];
        }

        fetch(filter, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                if (data?.results) {
                    setFetchedData(data?.results);
                    setShowDropdown(true);
                }
            })
            .catch(error => {
                console.error('Error fetching data:', error);
                setFetchedData([]);
                setShowDropdown(false);
            });
    }

    useEffect(() => {
        if (!fetchedData || !Array.isArray(fetchedData)) return;

        setOptions(prev => {
            const filterItems = refineSearchFilters?.[customPathname]?.tadvisors || [];

            // 1. Create a map to store final options (id -> item)
            const itemMap = new Map();

            // 2. Add fetched data (default isChecked false)
            fetchedData.forEach(item => {
                itemMap.set(item.id, { ...item, isChecked: false });
            });

            // 3. Add previously selected items — only if still checked
            (prev || []).forEach(item => {
                if (item.isChecked) {
                    itemMap.set(item.id, item);
                }
            });

            // 4. Apply current filters — mark filtered ones as checked
            filterItems.forEach(item => {
                itemMap.set(item.id, { ...item, isChecked: true });
            });

            return Array.from(itemMap.values());
        });
    }, [fetchedData, refineSearchFilters, customPathname]);

    const handleIndustryChange = ((e) => {
        const { name, checked, id } = e.target;
        let currentIndustry = [];

        if (name === "allSelect") {
            currentIndustry = options.map((industry) =>
                ({ ...industry, isChecked: checked })
            );
            setOptions(currentIndustry);
        } else {
            currentIndustry = options.map((industry) =>
                industry.id === parseInt(id) ? { ...industry, isChecked: checked } : industry
            );
            setOptions(currentIndustry);
        }
    });



    const selectedIndustryLength = options?.filter(option => option.isChecked)?.length ?? 0;
    const hasValue = companyVal || selectedIndustryLength > 0;

    return (
        <div className="relative">
            {/* Search Results Dropdown - Positioned BELOW the input */}
            {showDropdown && (fetchedData.length > 0 || options.some(option => option.isChecked)) && (
                <div ref={dropdownRef} className="absolute z-50 w-full top-full mt-1 bg-white border border-gray-100 rounded-sm shadow-md max-h-64 overflow-y-auto p-2">
                    <MultiSelectEntity
                        fetchData={fetchData}
                        handleEntityChange={handleIndustryChange}
                        primaryEntity={options}
                        setCompanyVal={setCompanyVal}
                        city="city"
                    />
                </div>
            )}
            
            <div className={`absolute left-3 md:left-5 transition-all duration-200 pointer-events-none flex items-center gap-1 md:gap-2 z-10 ${
                isFocused || hasValue 
                    ? 'top-0 -translate-y-1/2 text-xs bg-white px-1 text-[#BC8B20]' 
                    : 'top-1/2 -translate-y-1/2 text-xs md:text-sm text-gray-500'
            }`}>
                <span>{label}</span>
                {!isFocused && !hasValue && (
                    <span className="opacity-60 ml-1 md:ml-2 text-[10px] md:text-xs hidden sm:inline">e.g. Ambit, DC Advisory, Veda..</span>
                )}
                {(isFocused || hasValue) && selectedIndustryLength > 0 && (
                    <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                        {selectedIndustryLength}
                    </span>
                )}
            </div>
            
            <input
                ref={inputRef}
                type="text"
                value={companyVal}
                onChange={(e) => setCompanyVal(e.target.value)}
                onFocus={() => {
                    setIsFocused(true);
                    const hasSelectedOptions = options.some(option => option.isChecked);
                    if (fetchedData.length > 0 || hasSelectedOptions) {
                        setShowDropdown(true);
                    }
                }}
                onBlur={() => {
                    setTimeout(() => setIsFocused(false), 200);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                placeholder=""
            />
        </div>
    )
}

export default TAdvisor;
